import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInquiry } from '../../contexts/InquiryContext';

const Navbar: React.FC = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
    const location = useLocation();
    const { state } = useInquiry();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Clemens Briels', path: '/clemens-briels/' },
        { name: 'Antipodisme', path: '/antipodisme' },
        { name: 'Symboliek', path: '/symboliek' },
        { name: 'Collectie', path: '/#collectie' },
        { name: 'Over Ons', path: '/over-ons' }
    ];

    const inquiryCount = state.items.length;

    return (
        <header
            role="banner"
            className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled || isMobileMenuOpen ? 'bg-white/95 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-8'}`}
        >
            <nav role="navigation" aria-label="Hoofdnavigatie" className="container mx-auto px-8 flex justify-between items-center">
                <Link
                    to="/"
                    className="text-2xl font-serif font-bold tracking-tight text-primary hover:text-accent transition-colors duration-300 z-50"
                    aria-label="Kunstlounge Home"
                >
                    KUNSTLOUNGE
                </Link>

                {/* Desktop Navigation */}
                <ul className="hidden md:flex items-center space-x-12 text-[11px] font-bold tracking-[0.2em] uppercase">
                    {navLinks.map((link) => (
                        <li key={link.name}>
                            <Link
                                to={link.path}
                                className={`transition-colors duration-300 relative group py-2 ${location.pathname === link.path ? 'text-accent' : 'text-primary hover:text-accent'}`}
                                aria-label={link.name}
                            >
                                {link.name}
                                <span className={`absolute bottom-0 left-0 w-0 h-[1px] bg-accent transition-all duration-500 group-hover:w-full ${location.pathname === link.path ? 'w-full' : ''}`} aria-hidden="true"></span>
                            </Link>
                        </li>
                    ))}
                    <li className="flex items-center space-x-3 border-l pl-10 border-gray-200 ml-4">
                        <span className="font-bold text-primary" aria-current="true" aria-label="Nederlands (huidige taal)">NL</span>
                        <span className="text-gray-300 font-light" aria-hidden="true">|</span>
                        <a href="/en/" className="text-gray-600 hover:text-primary transition-colors duration-300" aria-label="Schakel taal naar Engels">EN</a>
                    </li>
                    <li>
                        <Link
                            to="/aanvraag"
                            className="relative bg-primary text-white px-8 py-3 rounded-sm hover:bg-accent transition-all duration-500 text-[10px] tracking-[0.2em] font-bold"
                            aria-label={`Bekijk aanvraaglijst (${inquiryCount} items)`}
                        >
                            AANVRAAG
                            {inquiryCount > 0 && (
                                <span className="ml-2 px-1.5 py-0.5 bg-accent text-white rounded-full text-[9px] animate-pulse">
                                    {inquiryCount}
                                </span>
                            )}
                        </Link>
                    </li>
                </ul>

                {/* Mobile Menu Trigger */}
                <button
                    className="md:hidden p-2 text-primary z-50 focus:outline-none flex items-center gap-3"
                    aria-label={isMobileMenuOpen ? "Sluit navigatiemenu" : "Open navigatiemenu"}
                    aria-expanded={isMobileMenuOpen}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    ref={mobileMenuButtonRef}
                >
                    {inquiryCount > 0 && !isMobileMenuOpen && (
                        <span className="text-[10px] font-bold text-accent bg-accent/10 px-2 py-1 rounded">
                            {inquiryCount} ITEMS
                        </span>
                    )}
                    <div className="w-6 h-5 relative flex flex-col justify-between">
                        <span className={`w-full h-0.5 bg-primary transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                        <span className={`w-full h-0.5 bg-primary transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                        <span className={`w-full h-0.5 bg-primary transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                    </div>
                </button>

                {/* Mobile Menu Overlay */}
                <div
                    className={`fixed inset-0 bg-white z-40 flex flex-col items-center justify-center transition-all duration-700 md:hidden ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none translate-y-4'}`}
                >
                    <ul className="text-center space-y-8">
                        {navLinks.map((link) => (
                            <li key={link.name}>
                                <Link
                                    to={link.path}
                                    className="text-4xl font-serif text-primary hover:text-accent transition-colors block py-2"
                                >
                                    {link.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-16 flex items-center space-x-8 text-sm tracking-widest uppercase mb-12">
                        <span className="font-bold text-accent" aria-current="true">NL</span>
                        <span className="text-gray-200">|</span>
                        <a href="/en/" className="text-gray-400 hover:text-primary transition-colors">EN</a>
                    </div>
                    <Link
                        to="/aanvraag"
                        className="bg-primary text-white px-16 py-5 rounded-sm hover:bg-accent transition-all duration-500 text-xs tracking-[0.3em] font-bold"
                    >
                        UW AANVRAAG {inquiryCount > 0 ? `(${inquiryCount})` : ''}
                    </Link>
                </div>
            </nav>
        </header>
    );
};

export default Navbar;